import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { providersApi, currenciesApi, providerRoutingApi, Provider, Currency, ProviderCurrency, ProviderRouting, CreateProviderRoutingRequest, ProviderViewDetails, ProviderCurrencyInput } from '../api/providers';
import { countriesApi, Country } from '../api/countries';
import { providerEventsApi } from '../api/providerEvents';
import { Plus, Settings, Building2, Eye, Pencil, PowerOff, Power, Archive } from 'lucide-react';
import Modal from '../components/common/Modal';

function ProvidersPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'providers' | 'routing'>('providers');
  const [providers, setProviders] = useState<Provider[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [routings, setRoutings] = useState<ProviderRouting[]>([]);
  const [problemEventCounts, setProblemEventCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showRoutingForm, setShowRoutingForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null);
  const [editingRouting, setEditingRouting] = useState<ProviderRouting | null>(null);
  type CurrencyFormItem = {
    currencyId: string;
    accountName: string;
    accountNumber: string;
    bankName: string;
    branchCode: string;
    bankCode: string;
    bankLocation: string;
    bankAddress: string;
    swiftCode: string;
    /** Only used on create. */
    initialBalance: string;
  };

  const emptyCurrencyForm = (currencyId: string): CurrencyFormItem => ({
    currencyId,
    accountName: '',
    accountNumber: '',
    bankName: '',
    branchCode: '',
    bankCode: '',
    bankLocation: '',
    bankAddress: '',
    swiftCode: '',
    initialBalance: '',
  });

  const emptyFormData = {
    countryId: '',
    name: '',
    currencies: [] as CurrencyFormItem[],
  };

  const [formData, setFormData] = useState<typeof emptyFormData>(emptyFormData);
  const [activeCurrencyTab, setActiveCurrencyTab] = useState<string>('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewDetails, setViewDetails] = useState<ProviderViewDetails | null>(null);
  const [viewError, setViewError] = useState('');
  const [activeViewTab, setActiveViewTab] = useState<string>('');
  const [viewTabMode, setViewTabMode] = useState<'banking' | 'ledger' | 'clients'>('banking');
  const [routingFormData, setRoutingFormData] = useState<CreateProviderRoutingRequest>({
    clientId: '00000000-0000-0000-0000-000000000000',
    currencyId: '',
    providerId: '',
    priority: 1,
    isActive: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());
  const [currencySearch, setCurrencySearch] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const [providersData, currenciesData, countriesData, routingData, eventCounts] = await Promise.all([
        providersApi.getAll(),
        currenciesApi.getAll(),
        countriesApi.getAll(true), // Only active countries
        providerRoutingApi.getAll(),
        providerEventsApi.problemCounts()
      ]);
      setProviders(providersData);
      setCurrencies(currenciesData);
      setCountries(countriesData.data || []);
      setRoutings(routingData);
      setProblemEventCounts(Object.fromEntries(eventCounts.map((item) => [item.providerId, item.count])));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const getCurrencyCodeById = (currencyId: string): string => {
    const raw = (currencyId || '').trim();
    if (!raw) return '';
    const byId = currencies.find((c) => (c.id || '').toUpperCase() === raw.toUpperCase());
    if (byId?.code) return byId.code;
    const byCode = currencies.find((c) => (c.code || '').toUpperCase() === raw.toUpperCase());
    if (byCode?.code) return byCode.code;
    return raw.toUpperCase();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate top-level required fields
    const errors = new Set<string>();
    if (!formData.countryId) errors.add('Country');
    if (!formData.name.trim()) errors.add('Provider Name');
    if (formData.currencies.length === 0) errors.add('Supported Currencies');

    // Validate banking per currency. Mark error per currency code so user knows which tab.
    const invalidTabs: string[] = [];
    for (const c of formData.currencies) {
      const code = getCurrencyCodeById(c.currencyId);
      const missing: string[] = [];
      if (!c.accountName.trim()) missing.push('Account Name');
      if (!c.accountNumber.trim()) missing.push('Account Number');
      if (!c.bankName.trim()) missing.push('Bank Name');
      if (!c.bankLocation.trim()) missing.push('Bank Location');
      if (!c.bankAddress.trim()) missing.push('Bank Address');
      if (!c.swiftCode.trim()) missing.push('SWIFT Code');
      if (missing.length > 0) {
        invalidTabs.push(code);
        missing.forEach((m) => errors.add(`${code}:${m}`));
      }
    }

    if (errors.size > 0) {
      const summary: string[] = [];
      if (errors.has('Country')) summary.push('Country');
      if (errors.has('Provider Name')) summary.push('Provider Name');
      if (errors.has('Supported Currencies')) summary.push('Supported Currencies');
      if (invalidTabs.length > 0) summary.push(`Banking details for: ${invalidTabs.join(', ')}`);
      setError(`Please fill in required fields: ${summary.join(', ')}`);
      setFieldErrors(errors);
      // Switch to first tab with errors so user sees them
      if (invalidTabs.length > 0) {
        const firstInvalid = formData.currencies.find((c) => invalidTabs.includes(getCurrencyCodeById(c.currencyId)));
        if (firstInvalid) setActiveCurrencyTab(firstInvalid.currencyId);
      }
      return;
    }

    setFieldErrors(new Set());

    try {
      setSubmitting(true);
      setError('');

      const buildCurrencyInputs = (): ProviderCurrencyInput[] =>
        formData.currencies.map((c) => {
          const input: ProviderCurrencyInput = {
            currencyId: c.currencyId,
            bankDetails: {
              accountName: c.accountName.trim(),
              accountNumber: c.accountNumber.trim(),
              bankName: c.bankName.trim(),
              branchCode: c.branchCode.trim() || null,
              bankCode: c.bankCode.trim() || null,
              bankLocation: c.bankLocation.trim(),
              bankAddress: c.bankAddress.trim(),
              swiftCode: c.swiftCode.trim().toUpperCase(),
            },
          };
          if (!editingProvider) {
            const raw = c.initialBalance.trim();
            if (raw) {
              const n = Number(raw);
              if (Number.isFinite(n) && n > 0) input.initialBalance = n;
            }
          }
          return input;
        });

      const payload = {
        countryId: formData.countryId,
        name: formData.name.trim(),
        currencies: buildCurrencyInputs(),
      };

      if (editingProvider) {
        await providersApi.update({ ...payload, id: editingProvider.id! });
      } else {
        await providersApi.create(payload);
      }

      setFormData(emptyFormData);
      setActiveCurrencyTab('');
      setShowCreateForm(false);
      setEditingProvider(null);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || `Failed to ${editingProvider ? 'update' : 'create'} provider`);
    } finally {
      setSubmitting(false);
    }
  };

  const hydrateCurrenciesFromProvider = (p: Provider): CurrencyFormItem[] => {
    return p.currencies.map((pc) => {
      const bd = pc.bankDetails ?? null;
      // If no per-currency banking yet (legacy provider), pre-fill from provider-level fields.
      const fallback = !bd;
      return {
        currencyId: pc.currencyId,
        accountName: bd?.accountName ?? (fallback ? p.accountName ?? '' : ''),
        accountNumber: bd?.accountNumber ?? (fallback ? p.accountNumber ?? '' : ''),
        bankName: bd?.bankName ?? (fallback ? p.bankName ?? '' : ''),
        branchCode: bd?.branchCode ?? (fallback ? p.branchCode ?? '' : ''),
        bankCode: bd?.bankCode ?? (fallback ? p.bankCode ?? '' : ''),
        bankLocation: bd?.bankLocation ?? (fallback ? p.bankLocation ?? '' : ''),
        bankAddress: bd?.bankAddress ?? (fallback ? p.bankAddress ?? '' : ''),
        swiftCode: bd?.swiftCode ?? (fallback ? p.swiftCode ?? '' : ''),
        initialBalance: '',
      };
    });
  };

  const handleEditProvider = async (provider: Provider) => {
    setEditingProvider(provider);
    setShowCreateForm(true);
    let source: Provider = provider;
    try {
      source = await providersApi.getById(provider.id!);
    } catch {
      // fall back to list data
    }
    const items = hydrateCurrenciesFromProvider(source);
    setFormData({
      countryId: source.countryId || '',
      name: source.name,
      currencies: items,
    });
    setActiveCurrencyTab(items[0]?.currencyId ?? '');
  };

  const handleViewProvider = (provider: Provider) => {
    if (!provider.id) return;
    navigate(`/providers/${provider.id}`);
  };

  const handleChangeStatus = async (provider: Provider) => {
    const isCurrentlyActive = (provider.status || 'ACTIVE') === 'ACTIVE';
    const willBeActive = !isCurrentlyActive;
    const message = `Are you sure you want to ${willBeActive ? 'activate' : 'deactivate'} ${provider.name}?`;
    
    if (!window.confirm(message)) {
      return;
    }

    try {
      setError('');
      await providersApi.changeStatus(provider.id!, willBeActive);
      
      // Refresh data and check if status changed
      await fetchData();
      
      // Find the provider after refresh to check its status
      const refreshedProviders = await providersApi.getAll();
      const refreshedProvider = refreshedProviders.find(p => p.id === provider.id);
      console.log('Provider before change:', provider.status);
      console.log('Provider after refresh:', refreshedProvider?.status);
      console.log('Expected status:', willBeActive ? 'ACTIVE' : 'SUSPENDED');
      console.log('All providers after refresh:', refreshedProviders.map(p => ({id: p.id, name: p.name, status: p.status})));
      
      if (refreshedProvider?.status === provider.status) {
        console.error('STATUS DID NOT CHANGE! Backend may not be updating the database.');
      } else {
        console.log('Status changed successfully!');
      }
    } catch (err: any) {
      console.error('Status change error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update provider status');
    }
  };

  const handleArchive = async (provider: Provider) => {
    const message = `Are you sure you want to archive ${provider.name}? This will move the provider to archive and cannot be undone.`;
    
    if (!window.confirm(message)) {
      return;
    }

    try {
      setError('');
      await providersApi.archive(provider.id!);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to archive provider');
    }
  };

  const toggleCurrency = (currencyId: string) => {
    const exists = formData.currencies.some((c) => c.currencyId === currencyId);
    setFormData((prev) => ({
      ...prev,
      currencies: exists
        ? prev.currencies.filter((c) => c.currencyId !== currencyId)
        : [...prev.currencies, emptyCurrencyForm(currencyId)],
    }));
    if (!exists) {
      setActiveCurrencyTab(currencyId);
    } else if (activeCurrencyTab === currencyId) {
      const remaining = formData.currencies.filter((c) => c.currencyId !== currencyId);
      setActiveCurrencyTab(remaining[0]?.currencyId ?? '');
    }
    if (fieldErrors.has('Supported Currencies')) {
      setFieldErrors((prev) => {
        const next = new Set(prev);
        next.delete('Supported Currencies');
        return next;
      });
    }
  };

  const updateCurrencyField = (currencyId: string, field: keyof CurrencyFormItem, value: string) => {
    setFormData((prev) => ({
      ...prev,
      currencies: prev.currencies.map((c) => (c.currencyId === currencyId ? { ...c, [field]: value } : c)),
    }));
    const code = getCurrencyCodeById(currencyId);
    const fieldLabelByKey: Partial<Record<keyof CurrencyFormItem, string>> = {
      accountName: 'Account Name',
      accountNumber: 'Account Number',
      bankName: 'Bank Name',
      bankLocation: 'Bank Location',
      bankAddress: 'Bank Address',
      swiftCode: 'SWIFT Code',
    };
    const label = fieldLabelByKey[field];
    if (label) {
      const errKey = `${code}:${label}`;
      if (fieldErrors.has(errKey)) {
        setFieldErrors((prev) => {
          const next = new Set(prev);
          next.delete(errKey);
          return next;
        });
      }
    }
  };

  const getCurrencyNames = (providerCurrencies: ProviderCurrency[]) => {
    if (!providerCurrencies || providerCurrencies.length === 0) {
      return 'No currencies';
    }
    return providerCurrencies
      .map(pc => pc.currency)
      .join(', ');
  };

  const getProviderName = (providerId: string): string => {
    const provider = providers.find(p => p.id === providerId);
    return provider?.name || 'Unknown';
  };

  const getProviderPriority = (providerId: string): string => {
    const providerRoutings = routings.filter(r => r.providerId === providerId && r.isActive);
    if (providerRoutings.length === 0) return 'Not set';
    
    // Get unique priorities sorted
    const uniquePriorities = Array.from(new Set(providerRoutings.map(r => r.priority)));
    const priorities = uniquePriorities.sort((a, b) => a - b);
    return priorities.join(', ');
  };

  const getCurrencyCode = (currencyId: string): string => {
    return getCurrencyCodeById(currencyId);
  };

  const handleRoutingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      if (editingRouting) {
        await providerRoutingApi.update(editingRouting.id!, routingFormData);
      } else {
        await providerRoutingApi.create(routingFormData);
      }

      await fetchData();
      setShowRoutingForm(false);
      setEditingRouting(null);
      setRoutingFormData({
        clientId: '00000000-0000-0000-0000-000000000000',
        currencyId: '',
        providerId: '',
        priority: 1,
        isActive: true
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save routing');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditRouting = (routing: ProviderRouting) => {
    setEditingRouting(routing);
    setRoutingFormData({
      clientId: routing.clientId,
      currencyId: routing.currencyId,
      providerId: routing.providerId,
      priority: routing.priority,
      isActive: routing.isActive
    });
    setShowRoutingForm(true);
  };

  const handleDeleteRouting = async (routing: ProviderRouting) => {
    const message = `Are you sure you want to delete this routing rule for ${getProviderName(routing.providerId)}?`;
    
    if (!window.confirm(message)) {
      return;
    }

    try {
      setError('');
      await providerRoutingApi.delete(routing.id!);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete routing rule');
    }
  };

  const getFieldClassName = (fieldName: string): string => {
    const baseClass = "w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500";
    const errorClass = fieldErrors.has(fieldName) 
      ? "border-red-300 bg-red-50" 
      : "border-gray-300";
    return `${baseClass} ${errorClass}`;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Providers</h1>
          <p className="text-gray-600">Manage payment providers and their supported currencies</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Providers</h1>
        <p className="text-gray-600">Manage payment providers and their routing settings</p>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('providers')}
              className={`${
                activeTab === 'providers'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <Building2 className="w-4 h-4 mr-2" />
              Providers
            </button>
            <button
              onClick={() => setActiveTab('routing')}
              className={`${
                activeTab === 'routing'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <Settings className="w-4 h-4 mr-2" />
              Routing Settings
            </button>
          </nav>
        </div>
      </div>

      {/* Providers Tab Content */}
      {activeTab === 'providers' && (
        <>
          <div className="mb-4 flex justify-end">
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Provider
            </button>
          </div>

      <Modal
        isOpen={showCreateForm}
        onClose={() => {
          setShowCreateForm(false);
          setEditingProvider(null);
          setFormData(emptyFormData);
          setActiveCurrencyTab('');
          setCurrencySearch('');
          setError('');
          setFieldErrors(new Set());
        }}
        title={editingProvider ? "Edit Provider" : "Create New Provider"}
        size="lg"
      >
        <form onSubmit={handleSubmit} noValidate>
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country *
            </label>
            <select
              value={formData.countryId}
              onChange={(e) => {
                setFormData({ ...formData, countryId: e.target.value });
                if (fieldErrors.has('Country')) {
                  setFieldErrors(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('Country');
                    return newSet;
                  });
                }
              }}
              className={getFieldClassName('Country')}
            >
              <option value="">Select Country</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name} ({country.code})
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Provider Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (fieldErrors.has('Provider Name')) {
                  setFieldErrors(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('Provider Name');
                    return newSet;
                  });
                }
              }}
              className={getFieldClassName('Provider Name')}
              placeholder="Enter provider name"
              />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Supported Currencies *
            </label>
            
            {/* Search input */}
            <div className="mb-3">
              <input
                type="text"
                placeholder="Search currencies..."
                value={currencySearch}
                onChange={(e) => setCurrencySearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Selected currencies display */}
            <div className={`mb-3 min-h-[60px] p-3 border rounded-lg ${fieldErrors.has('Supported Currencies') ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
              {formData.currencies.length === 0 ? (
                <p className="text-sm text-gray-500">No currencies selected</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {formData.currencies.map((c) => {
                    const currency = currencies.find((x) => x.id === c.currencyId);
                    if (!currency) return null;
                    return (
                      <span
                        key={c.currencyId}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                      >
                        {currency.code}
                        <button
                          type="button"
                          onClick={() => toggleCurrency(c.currencyId)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Scrollable currency list */}
            <div className={`border rounded-lg ${fieldErrors.has('Supported Currencies') ? 'border-red-300' : 'border-gray-300'}`}>
              <div className="max-h-48 overflow-y-auto">
                {currencies
                  .filter(currency => 
                    currencySearch === '' || 
                    currency.code.toLowerCase().includes(currencySearch.toLowerCase()) ||
                    currency.name.toLowerCase().includes(currencySearch.toLowerCase())
                  )
                  .map((currency) => (
                  <label
                    key={currency.id}
                    className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={formData.currencies.some((c) => c.currencyId === currency.id)}
                      onChange={() => toggleCurrency(currency.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-sm font-medium">{currency.code}</span>
                    <span className="ml-2 text-xs text-gray-500">({currency.name})</span>
                  </label>
                ))}
              </div>
            </div>
            
            {fieldErrors.has('Supported Currencies') && (
              <p className="mt-1 text-sm text-red-600">Please select at least one currency</p>
            )}
          </div>

          {/* Banking Information per currency (tabs) */}
          {formData.currencies.length > 0 && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Banking Information</h3>
              <p className="text-xs text-gray-500 mb-4">
                Each selected currency has its own banking details. Use the tabs below to switch between currencies.
              </p>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-4">
                <nav className="-mb-px flex flex-wrap gap-x-1">
                  {formData.currencies.map((c) => {
                    const code = getCurrencyCodeById(c.currencyId);
                    const isActive = activeCurrencyTab === c.currencyId;
                    const hasError = Array.from(fieldErrors).some((k) => k.startsWith(`${code}:`));
                    return (
                      <button
                        type="button"
                        key={c.currencyId}
                        onClick={() => setActiveCurrencyTab(c.currencyId)}
                        className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                          isActive
                            ? 'border-blue-500 text-blue-600'
                            : hasError
                              ? 'border-transparent text-red-600 hover:text-red-700'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {code}
                        {hasError && <span className="ml-1 text-xs">!</span>}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Active tab content */}
              {(() => {
                const active = formData.currencies.find((c) => c.currencyId === activeCurrencyTab) ?? formData.currencies[0];
                if (!active) return null;
                const code = getCurrencyCodeById(active.currencyId);
                const fieldErrKey = (label: string) => `${code}:${label}`;
                const tabFieldClass = (label: string) => {
                  const base = "w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500";
                  return fieldErrors.has(fieldErrKey(label))
                    ? `${base} border-red-300 bg-red-50`
                    : `${base} border-gray-300`;
                };
                return (
                  <div key={active.currencyId}>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Account Name *</label>
                        <input
                          type="text"
                          value={active.accountName}
                          onChange={(e) => updateCurrencyField(active.currencyId, 'accountName', e.target.value)}
                          className={tabFieldClass('Account Name')}
                          placeholder="Enter account name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Account Number *</label>
                        <input
                          type="text"
                          value={active.accountNumber}
                          onChange={(e) => updateCurrencyField(active.currencyId, 'accountNumber', e.target.value)}
                          className={tabFieldClass('Account Number')}
                          placeholder="Enter account number"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name *</label>
                        <input
                          type="text"
                          value={active.bankName}
                          onChange={(e) => updateCurrencyField(active.currencyId, 'bankName', e.target.value)}
                          className={tabFieldClass('Bank Name')}
                          placeholder="Enter bank name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Branch Code</label>
                        <input
                          type="text"
                          value={active.branchCode}
                          onChange={(e) => updateCurrencyField(active.currencyId, 'branchCode', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter branch code"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Bank Code</label>
                        <input
                          type="text"
                          value={active.bankCode}
                          onChange={(e) => updateCurrencyField(active.currencyId, 'bankCode', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter bank code"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Bank Location *</label>
                        <input
                          type="text"
                          value={active.bankLocation}
                          onChange={(e) => updateCurrencyField(active.currencyId, 'bankLocation', e.target.value)}
                          className={tabFieldClass('Bank Location')}
                          placeholder="Enter bank location"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Bank Address *</label>
                      <textarea
                        value={active.bankAddress}
                        onChange={(e) => updateCurrencyField(active.currencyId, 'bankAddress', e.target.value)}
                        className={tabFieldClass('Bank Address')}
                        placeholder="Enter bank address"
                        rows={2}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">SWIFT Code *</label>
                        <input
                          type="text"
                          value={active.swiftCode}
                          onChange={(e) => updateCurrencyField(active.currencyId, 'swiftCode', e.target.value.toUpperCase())}
                          className={tabFieldClass('SWIFT Code')}
                          placeholder="Enter SWIFT code"
                          maxLength={11}
                        />
                      </div>
                      {!editingProvider && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Initial balance ({code})
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={active.initialBalance}
                            onChange={(e) => updateCurrencyField(active.currencyId, 'initialBalance', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Optional, leave blank for 0"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setShowCreateForm(false);
                setEditingProvider(null);
                setFormData(emptyFormData);
                setActiveCurrencyTab('');
                setCurrencySearch('');
                setError('');
                setFieldErrors(new Set());
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting 
                ? (editingProvider ? 'Updating...' : 'Creating...') 
                : (editingProvider ? 'Update Provider' : 'Create Provider')}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setViewDetails(null);
          setViewError('');
          setActiveViewTab('');
          setViewTabMode('banking');
        }}
        title="Provider details"
        size="xl"
      >
        {viewDetails && (() => {
          const ACCOUNT_TYPE_META: Record<string, { display: string; purpose: string }> = {
            SETTLEMENT:           { display: 'Settlement',            purpose: 'Main account at bank / provider' },
            CLEARING:             { display: 'Clearing',              purpose: 'Intermediate processing layer' },
            SUSPENSE:             { display: 'Suspense',              purpose: 'Temporary hold for unclear amounts' },
            ERROR:                { display: 'Error',                 purpose: 'Errors pending investigation' },
            FEE_INCOME:           { display: 'Fee Income',            purpose: 'Commission income' },
            FX_INCOME:            { display: 'FX Income',             purpose: 'FX conversion income' },
            OTHER_INCOME:         { display: 'Other Income',          purpose: 'Miscellaneous income' },
            PROVIDER_FEE_EXPENSE: { display: 'Provider Fee Expense',  purpose: 'Provider commission expenses' },
            PROVIDER_FX_EXPENSE:  { display: 'Provider FX Expense',   purpose: 'Provider FX expenses' },
          };
          const ORDER = ['SETTLEMENT','CLEARING','SUSPENSE','ERROR','FEE_INCOME','FX_INCOME','OTHER_INCOME','PROVIDER_FEE_EXPENSE','PROVIDER_FX_EXPENSE'];

          const currencies = viewDetails.provider?.currencies ?? [];
          const currencyCodes = Array.from(
            new Set(
              viewDetails.ledgerAccounts
                .map(r => r.currencyId)
                .concat(currencies.map(c => c.currency || c.currencyId))
            )
          ).filter(Boolean);

          const currentViewTab = activeViewTab || currencyCodes[0] || '';

          const tabAccounts = viewDetails.ledgerAccounts.filter(
            r => r.currencyId === currentViewTab
          );
          const accountByType = Object.fromEntries(tabAccounts.map(r => [r.accountTypeCode, r]));
          const tabCurrencyTotal = viewDetails.totalsByCurrency.find(
            t => (t.currencyCode || t.currencyId) === currentViewTab
          );
          const tabProviderCurrency = currencies.find(
            c => (c.currency || c.currencyId) === currentViewTab
          );
          const bd = tabProviderCurrency?.bankDetails;

          return (
            <div className="max-h-[75vh] overflow-y-auto pr-1">
              {/* Provider header */}
              <div className="grid grid-cols-2 gap-3 text-sm bg-gray-50 rounded-lg p-3 mb-4">
                <div><span className="text-gray-500">Name</span><br /><span className="font-medium">{viewDetails.provider?.name}</span></div>
                <div><span className="text-gray-500">Status</span><br />
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    (viewDetails.provider?.status || 'ACTIVE') === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                  }`}>{viewDetails.provider?.status || 'ACTIVE'}</span>
                </div>
              </div>

              {/* Currency tabs */}
              {currencyCodes.length === 0 ? (
                <p className="text-sm text-gray-500">No currencies configured.</p>
              ) : (
                <>
                  <div className="flex gap-1 border-b border-gray-200 mb-4 overflow-x-auto">
                    {currencyCodes.map(code => (
                      <button
                        key={code}
                        type="button"
                        onClick={() => setActiveViewTab(code)}
                        className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                          currentViewTab === code
                            ? 'border-indigo-600 text-indigo-700'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {code}
                        {tabCurrencyTotal && currentViewTab === code && (
                          <span className="ml-2 text-xs text-gray-400 font-normal">
                            {(viewDetails.totalsByCurrency.find(t => (t.currencyCode || t.currencyId) === code)?.totalBalance ?? 0)
                              .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Sub-tabs: Banking | Ledger | Clients */}
                  <div className="flex gap-1 border-b border-gray-200 mb-4">
                    <button
                      type="button"
                      onClick={() => setViewTabMode('banking')}
                      className={`px-3 py-1.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                        viewTabMode === 'banking'
                          ? 'border-teal-600 text-teal-700'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Banking Details
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewTabMode('ledger')}
                      className={`px-3 py-1.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                        viewTabMode === 'ledger'
                          ? 'border-indigo-600 text-indigo-700'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Ledger Accounts
                      {tabAccounts.length > 0 && (
                        <span className="ml-1 text-xs text-gray-400">({tabAccounts.length})</span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewTabMode('clients')}
                      className={`px-3 py-1.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                        viewTabMode === 'clients'
                          ? 'border-purple-600 text-purple-700'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Clients
                      {(() => {
                        const clientCount = viewDetails.clientAccounts?.filter(c => c.currencyId === currentViewTab || c.currencyCode === currentViewTab).length ?? 0;
                        return clientCount > 0 ? <span className="ml-1 text-xs text-gray-400">({clientCount})</span> : null;
                      })()}
                    </button>
                  </div>

                  {/* Content based on selected tab mode */}
                  {viewTabMode === 'banking' && (
                    <div className="mb-4">
                      {!bd ? (
                        <p className="text-sm text-amber-600">No banking details configured for {currentViewTab}.</p>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm bg-gray-50 rounded-lg p-3">
                          <div><span className="text-gray-500">Account name</span><br /><span className="font-medium">{bd.accountName || '—'}</span></div>
                          <div><span className="text-gray-500">Account number</span><br /><span className="font-medium font-mono text-xs">{bd.accountNumber || '—'}</span></div>
                          <div><span className="text-gray-500">Bank</span><br /><span className="font-medium">{bd.bankName || '—'}</span></div>
                          <div><span className="text-gray-500">SWIFT</span><br /><span className="font-medium font-mono text-xs">{bd.swiftCode || '—'}</span></div>
                          <div><span className="text-gray-500">Branch / Code</span><br /><span className="font-medium">{bd.branchCode || '—'} / {bd.bankCode || '—'}</span></div>
                          <div><span className="text-gray-500">Location</span><br /><span className="font-medium">{bd.bankLocation || '—'}</span></div>
                          {bd.bankAddress && <div className="col-span-2 sm:col-span-3"><span className="text-gray-500">Bank address</span><br /><span className="font-medium">{bd.bankAddress}</span></div>}
                        </div>
                      )}
                    </div>
                  )}

                  {viewTabMode === 'ledger' && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Ledger Accounts</h3>
                        {tabCurrencyTotal && (
                          <span className="text-xs text-gray-500">
                            Total: <span className="font-mono font-semibold text-gray-700">
                              {tabCurrencyTotal.totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currentViewTab}
                            </span>
                          </span>
                        )}
                      </div>
                      {ORDER.length === 0 || tabAccounts.length === 0 ? (
                        <p className="text-sm text-gray-500">No ledger accounts linked for {currentViewTab}.</p>
                      ) : (
                        <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Type</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Display name</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Purpose</th>
                              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ORDER.map(typeCode => {
                              const row = accountByType[typeCode];
                              const meta = ACCOUNT_TYPE_META[typeCode];
                              return (
                                <tr key={typeCode} className={`border-t border-gray-100 ${
                                  row ? '' : 'opacity-40'
                                }`}>
                                  <td className="px-3 py-2 font-mono text-xs text-gray-500">{typeCode}</td>
                                  <td className="px-3 py-2 font-medium text-gray-800">
                                    {meta?.display ?? typeCode} <span className="text-gray-400 font-normal">{currentViewTab}</span>
                                  </td>
                                  <td className="px-3 py-2 text-gray-500 text-xs">{meta?.purpose ?? ''}</td>
                                  <td className="px-3 py-2 text-right font-mono text-sm">
                                    {row
                                      ? (row.balance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                      : <span className="text-gray-300">—</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                  {viewTabMode === 'clients' && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Client Accounts</h3>
                        {(() => {
                          const clientsForTab = viewDetails.clientAccounts?.filter(c => c.currencyId === currentViewTab || c.currencyCode === currentViewTab) ?? [];
                          const totalBalance = clientsForTab.reduce((sum, c) => sum + (c.totalBalance ?? 0), 0);
                          return clientsForTab.length > 0 ? (
                            <span className="text-xs text-gray-500">
                              {clientsForTab.length} client{clientsForTab.length === 1 ? '' : 's'} · Total: <span className="font-mono font-semibold text-gray-700">
                                {totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currentViewTab}
                              </span>
                            </span>
                          ) : null;
                        })()}
                      </div>
                      {(() => {
                        const clientsForTab = viewDetails.clientAccounts?.filter(c => c.currencyId === currentViewTab || c.currencyCode === currentViewTab) ?? [];
                        if (clientsForTab.length === 0) {
                          return <p className="text-sm text-gray-500">No client accounts for {currentViewTab}.</p>;
                        }
                        return (
                          <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Client</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Available</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Blocked</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clientsForTab.map((client) => (
                                <tr key={client.id} className="border-t border-gray-100">
                                  <td className="px-3 py-2">
                                    <div className="font-medium text-gray-800">{client.clientFullName}</div>
                                    {client.accountName && (
                                      <div className="text-xs text-gray-500">{client.accountName}</div>
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                      client.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {client.status}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-right font-mono text-sm">
                                    {(client.availableBalance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="px-3 py-2 text-right font-mono text-sm text-amber-600">
                                    {(client.blockedBalance ?? 0) > 0
                                      ? (client.blockedBalance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                      : '—'}
                                  </td>
                                  <td className="px-3 py-2 text-right font-mono text-sm font-semibold">
                                    {(client.totalBalance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        );
                      })()}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}
        {!viewDetails && viewError && (
          <div className="text-red-600 text-sm py-4">{viewError}</div>
        )}
      </Modal>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Provider Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Supported Currencies
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Currency Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Event Issues
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {providers.map((provider, index) => (
                <tr key={provider.id || index}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{provider.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      (provider.status || 'ACTIVE') === 'ACTIVE' 
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {provider.status || 'ACTIVE'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {getCurrencyNames(provider.currencies)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {provider.currencies?.length || 0} {provider.currencies?.length === 1 ? 'currency' : 'currencies'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {getProviderPriority(provider.id!)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(provider.id && problemEventCounts[provider.id]) ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {problemEventCounts[provider.id]}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">0</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleViewProvider(provider)}
                        title="View"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-teal-600 hover:bg-teal-50 hover:text-teal-900 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="hidden xl:inline">View</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleEditProvider(provider)}
                        title="Edit"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-indigo-600 hover:bg-indigo-50 hover:text-indigo-900 transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                        <span className="hidden xl:inline">Edit</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleChangeStatus(provider)}
                        title={(provider.status || 'ACTIVE') === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                          (provider.status || 'ACTIVE') === 'ACTIVE'
                            ? 'text-orange-600 hover:bg-orange-50 hover:text-orange-900'
                            : 'text-green-600 hover:bg-green-50 hover:text-green-900'
                        }`}
                      >
                        {(provider.status || 'ACTIVE') === 'ACTIVE'
                          ? <><PowerOff className="w-4 h-4" /><span className="hidden xl:inline">Deactivate</span></>
                          : <><Power className="w-4 h-4" /><span className="hidden xl:inline">Activate</span></>}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleArchive(provider)}
                        title="Archive"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-red-600 hover:bg-red-50 hover:text-red-900 transition-colors"
                      >
                        <Archive className="w-4 h-4" />
                        <span className="hidden xl:inline">Archive</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {providers.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      No providers found. Click "Add Provider" to create one.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* Routing Tab Content */}
      {activeTab === 'routing' && (
        <>
          <div className="mb-4 flex justify-end">
            <button
              onClick={() => setShowRoutingForm(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Routing
            </button>
          </div>

          <Modal
            isOpen={showRoutingForm}
            onClose={() => {
              setShowRoutingForm(false);
              setEditingRouting(null);
              setRoutingFormData({
                clientId: '00000000-0000-0000-0000-000000000000',
                currencyId: '',
                providerId: '',
                priority: 1,
                isActive: true
              });
              setError('');
            }}
            title={editingRouting ? 'Edit Provider Routing' : 'Add Provider Routing'}
            size="lg"
          >
            <form onSubmit={handleRoutingSubmit}>
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency
                </label>
                <select
                  value={routingFormData.currencyId}
                  onChange={(e) => setRoutingFormData({ ...routingFormData, currencyId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Currency</option>
                  {currencies.map((currency) => (
                    <option key={currency.id} value={currency.id}>
                      {currency.code} - {currency.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Provider
                  </label>
                  <select
                    value={routingFormData.providerId}
                    onChange={(e) => setRoutingFormData({ ...routingFormData, providerId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Provider</option>
                    {providers.map((provider) => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={routingFormData.priority}
                    onChange={(e) => setRoutingFormData({ ...routingFormData, priority: parseInt(e.target.value) || 1 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={routingFormData.isActive}
                    onChange={(e) => setRoutingFormData({ ...routingFormData, isActive: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowRoutingForm(false);
                    setEditingRouting(null);
                    setRoutingFormData({
                      clientId: '00000000-0000-0000-0000-000000000000',
                      currencyId: '',
                      providerId: '',
                      priority: 1,
                      isActive: true
                    });
                    setError('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Saving...' : (editingRouting ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </Modal>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Currency
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Provider
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {routings.map((routing) => (
                  <tr key={routing.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {routing.currencyCode || getCurrencyCode(routing.currencyId)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {getProviderName(routing.providerId)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{routing.priority}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEditRouting(routing)}
                        className="text-blue-600 hover:text-blue-900 mr-2"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteRouting(routing)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {routings.length === 0 && (
              <div className="text-center py-12">
                <Settings className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No routing rules</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by adding a new provider routing rule.
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ProvidersPage;
